import ConfigResolver from './config/ConfigResolver';
import ImplicitLogSchemas from './config/ImplicitLogSchemas';
import Impression from './events/Impression';
import TTILog from './modules/TTILog/TTILog';
import FCPLog from './modules/FCPLog/FCPLog';
import FMPLog from './modules/FMPLog/FMPLog';
import LCPLog from './modules/LCPLog/LCPLog';
import FIDLog from './modules/FIDLog/FIDLog';
import PageViewLog from './modules/PageViewLog/PageViewLog';
import CommonParameter from './parameters/CommonParameter';
import CommonWebParameter from './parameters/CommonWebParameter';
import MetaParameter from './parameters/MetaParameter';
import DataParameter from './parameters/DataParameter';
import ExtraParameter from './parameters/ExtraParameter';
import CampaignParameter from './parameters/CampaignParameter';
import ParameterStore from './parameters/ParameterStore';
import WebLogRequester from './requesters/WebLogRequester';
import AppLogRequester from './requesters/AppLogRequester';
import LogInterceptor from './requesters/LogInterceptor';
import LogRequester from './requesters/LogRequester';
import AttrDataUtil from './utils/AttrDataUtil';
import WindowEventListener from './utils/WindowEventListener';
import ConsoleLog from './utils/ConsoleLog';
import PlatformUtil from './utils/PlatformUtil';
import RequestUtil from './utils/RequestUtil';
import Click from './events/Click';

const TAG = 'WebLog';
const SCRIPT_TAG_SELECTOR = 'coupang-web-log';
const AUTO_PAGEVIEW_ELEMENT_SELECTOR = '[data-web-log-event=pageview]';
const AUTO_BIND_EVENT_TYPES = ['click', 'mouseenter', 'mouseleave', 'hold', 'scroll', 'swipe', 'keyup', 'keydown'];
const INIT_ATTRIBUTE_NAMES = {
  APP_CODE: 'data-app-code',
  HOST: 'data-host',
  PLATFORM: 'data-platform',
  MARKET: 'data-market',
  SERVICE: 'data-service',
  MODE: 'data-mode',
  AUTO_SEND_PAGEVIEW_LOG: 'data-auto-send-pageview',
  TTI: 'data-tti',
  V2_API: 'data-v2-api',
  API_VERSION: 'data-api-version',
  WITH_CREDENTIAL: 'data-with-credential',
  FMP: 'data-fmp',
  AUTO_SEND_SPA_PAGEVIEW: 'data-auto-send-spa-pageview',
  PAGE_NAME: 'data-page-name',
  DOMAIN: 'data-domain',
  TRACE_ID: 'data-web-log-trace-id',
  SERVER_TIME: 'data-web-log-server-time',
  SDP_VISIT_KEY: 'data-sdp-visit-key',
  WEB_BUILD_NO: 'data-web-build-no', // docker image tag from domain
  IMPRESSION_AREA_THRESHOLD: 'data-web-log-impression-area',
  IMPRESSION_TIME_THRESHOLD: 'data-web-log-impression-time',
  DISABLE_FETCH_METHOD: 'data-disable-fetch-method'
};

export interface DefaultOptionsType {
  reportSpaPageViewLog?: boolean;
  [key: string]: unknown;
}

/**
 * This class provides Coupang App, Web, Mobile Web PageView and Event Logging.
 */
export default class WebLog {
  private platform: string;
  private campaignParameter: CampaignParamType;
  private commonParameter: CommonParameter;
  private webLogRequester: WebLogRequester;
  private appLogRequester: AppLogRequester;
  private logRequester: LogRequester;
  private logInterceptor: LogInterceptor<object>;
  private parameterStore: ParameterStore;
  private configResolver: ConfigResolver;
  private commonData: CommonDataType;
  private autoSendPageView: boolean;
  private initialized: boolean;

  constructor() {
    // Do not init WebLog more than once.
    if (window.CoupangWebLog) {
      return;
    }

    this.initialized = false;

    const scriptEl = document.getElementById(SCRIPT_TAG_SELECTOR);
    // the interceptor need register before other weblog send in init method
    this.logInterceptor = new LogInterceptor();

    if (scriptEl) {
      const mode = (scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.MODE) || 'production') as Mode;
      mode !== 'production' && ConsoleLog.setDebugMode();

      const elCustomData = {
        reportSpaPageViewLog: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.AUTO_SEND_SPA_PAGEVIEW)
      };

      const initData: InitParams = {
        appCode: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.APP_CODE) || undefined,
        host: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.HOST) || undefined,
        platform: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.PLATFORM) || undefined,
        market: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.MARKET) || undefined,
        service: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.SERVICE) || undefined,
        domain: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.DOMAIN) || undefined,
        pageName: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.PAGE_NAME) || '',
        traceId: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.TRACE_ID) || undefined,
        serverTime: parseInt(scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.SERVER_TIME), 10) || Date.now(),
        impressionAreaThreshold: parseFloat(scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.IMPRESSION_AREA_THRESHOLD)) || undefined,
        impressionTimeThreshold: parseFloat(scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.IMPRESSION_TIME_THRESHOLD)) || undefined,
        mode: mode,
        useAutoSendPageViewLog: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.AUTO_SEND_PAGEVIEW_LOG) === 'true',
        tti: JSON.parse(scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.TTI)),
        
        fmp: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.FMP) === 'true',
        reportSpaPageViewLog: elCustomData.reportSpaPageViewLog === null ? true : scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.AUTO_SEND_SPA_PAGEVIEW) === 'true',
        sdpVisitKey: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.SDP_VISIT_KEY) || '',
        webBuildNo: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.WEB_BUILD_NO) || '',
        disableFetchMethod: scriptEl.getAttribute(INIT_ATTRIBUTE_NAMES.DISABLE_FETCH_METHOD) === 'true'
      };
     
      ConsoleLog.d(TAG, '#constructor - use custom attribute(data-platform, data-service, data-mode, data-auto-send-pageview) for initialize');
      this.init(initData);
    } else {
      ConsoleLog.d(TAG, '#constructor - Not found script tag(#coupang-web-log) for initialize');
    }
  }

  /**
   * Initialize WebLog.
   *
   * @param initData {object} data for initialize
   * - appCode {string} Specify a application code registered in LJ Portal
   * - host {string} (Optional) Specify a custom host url. Will ignore the default url generated by 'mode' property. Must starts with 'https://'.
   * - platform {string} In case of webview, value of the platform parameter is ignored and set automatically according to OS(IOS, ANDROID).
   *          android | ios | web | mweb
   * - market {string} Specify a market of the service.
   *            KR | TW
   * - service {string} In case of webview, value of service parameter is ignored and it is determined by Android and iOS Web Log library.
   *          coupang | wing | ...
   * - mode {string} default value is "production"
   *          develop | integration | production
   * - tti {object} Configuration of TTILogger
   * - useAutoSendPageViewLog {boolean} automatically send logs when the page loads.
   * - reportSpaPageViewLog {boolean} automatically send pageview logs for SPA pages. Default to "true".
   * @returns {WebLog} instance of WebLog.
   */
  init(initData: InitParams): WebLog {
    const { tti, host, serverTime, impressionAreaThreshold, impressionTimeThreshold, appCode = 'coupang', mode = 'production', platform = 'web', market = 'KR', service = 'coupang', domain = 'coupang', pageName = '', traceId = '', useAutoSendPageViewLog = false,  reportSpaPageViewLog = true, sdpVisitKey = '', webBuildNo = 'default', disableFetchMethod = false,  } = initData;

    ConsoleLog.d(TAG, `#init -\n${JSON.stringify(initData, undefined, 2)}`);

    this.configResolver = new ConfigResolver({
      appCode: appCode || service,
      host,
      market,
      mode
    });

    this.webLogRequester = new WebLogRequester(
      {
        ...this.configResolver.resolveEndpoints(),
        disableFetchMethod
      },
      this.logInterceptor
    );
    this.appLogRequester = new AppLogRequester(this.logInterceptor);
    this.logRequester = new LogRequester(this.webLogRequester, this.appLogRequester);
    this.campaignParameter = CampaignParameter.getCampaignParameter();
    this.platform = PlatformUtil.getPlatform(platform);
    // set platform, web build number and webSessionId
    this.commonParameter = new CommonParameter()
      .setPlatform(this.platform)
      .setWebBuildNo(webBuildNo)
      .setWebSessionId();
    this.parameterStore = new ParameterStore();

    // Because these fields should be added to data, not common,
    // we want to keep them separately.
    this.commonData = {
      domain,
      pageName,
      serverTime,
      traceId: AttrDataUtil.getCleanTraceId(traceId),
      sdpVisitKey
    };

    this.autoSendPageView = useAutoSendPageViewLog;
    if (this.autoSendPageView) {
      // Trigger implicit page view
      this.autoSendPageViewLog();
    } else {
      // When no pageview event is triggered, we still need to generate common web parameters.
      this.commonParameter.setCommonWebParameter(new CommonWebParameter());
      // For implicit log schemas requiring pvid in data.
      this.commonData.pvid = this.commonParameter.getJSON()?.common?.web?.pvid || '';
    }

    // Bind events
    window.addEventListener('DOMContentLoaded', () => {
      // Bind implicit events
      this.trackImpression({
        options: { areaThreshold: impressionAreaThreshold, timeThreshold: impressionTimeThreshold }
      });
      this.trackClick();

      this.bindEvent(AUTO_BIND_EVENT_TYPES);
    });

    if (tti && !window.ttiSubmit) {
      const ttiLogger = new TTILog(this.webLogRequester, this.appLogRequester);
      ConsoleLog.d(TAG, "#init - Active 'tti-logger'");

      ttiLogger.init({
        platform,
        webBuildNo,
        ...tti
      });

      window.ttiSubmit = ttiLogger.manualRecord.bind(ttiLogger);
      window.ttiRecord = ttiLogger.record.bind(ttiLogger);
      window.ttiStop = ttiLogger.stop.bind(ttiLogger);
      window.logImageLoadTime = ttiLogger.logImageLoadTime.bind(ttiLogger);
    }

    try {
      this.initPerfMetrics({
        platform,
        webBuildNo,
        ...tti
      });

      if (reportSpaPageViewLog) {
        this.initSpaPageViewLog({
          platform,
          webBuildNo,
          ...tti
        });
      }
    } catch (e) {
      ConsoleLog.e(TAG, `initPerfMetrics or initPageViewLog error: ${e.message}`);
    }

    this.initialized = true;

    return this;
  }

  initPerfMetrics(fields: PerfInitFields, shouldInitFMP: boolean = false) {
    [
      {
        name: 'FCP',
        constructor: FCPLog
      },
      {
        name: 'FMP',
        constructor: FMPLog
      },
      {
        name: 'LCP',
        constructor: LCPLog
      },
      {
        name: 'FID',
        constructor: FIDLog
      }
    ].forEach(({ name, constructor }) => {
      if (shouldInitFMP || name !== 'FMP') {
        const logger = new constructor(this.webLogRequester, this.appLogRequester);
        logger.init(fields);
      }
    });
  }

  /**
   * Run handlers before weblog sending
   * @param callback before send, callback is called with returned params
   */
  public useRequestInterceptor(callback: (param: RequestParams) => RequestParams | Promise<RequestParams>) {
    this.logInterceptor.useRequestInterceptor(callback);
  }

  /**
   * Init pageView for SPA
   * @param fields
   */
  initSpaPageViewLog(fields: PerfInitFields) {
    const pageViewLog = new PageViewLog(this.webLogRequester, this.appLogRequester, this.autoSendPageView);
    pageViewLog.init(fields);
  }

  /**
   * Send the implicit page view log
   */
  autoSendPageViewLog() {
    const pageViewEl = document.querySelector(AUTO_PAGEVIEW_ELEMENT_SELECTOR);
    const requestParam = this.getRequestParam(pageViewEl);
    const params = this.parameterStore.replaceDynamicString(requestParam);

    // For implicit log schemas requiring pvid in data
    this.commonParameter.setCommonWebParameter(new CommonWebParameter());
    this.commonData.pvid = this.commonParameter.getJSON()?.common?.web?.pvid || '';

    const updatedParams = RequestUtil.generateImplicitLogRequestParams(ImplicitLogSchemas.PageView, params, this.commonData);
    const requestJSON = RequestUtil.generateRequestJSON(updatedParams, this.commonParameter, this.campaignParameter);
    this.logRequester.send(requestJSON, true);
    ConsoleLog.d(TAG, `#autoSendPageViewLog data: ${JSON.stringify(requestJSON)}`);
    ConsoleLog.d(TAG, '#autoSendPageViewLog - sent');
  }

  /**
   * Register parameter
   * @param registerKey key for request parameter
   * @param jsonData {object} JSON data
   */
  registerParameter(registerKey: string, jsonData: Record<string, unknown>) {
    this.parameterStore.register(registerKey, jsonData);
  }

  /**
   * Get parameter
   * @param registerKey key for request parameter
   * @returns {string | number | object}
   */
  getRegisteredParameter(registerKey: string): unknown {
    return this.parameterStore.getRegisteredData(registerKey);
  }

  /**
   * Set event (data-web-log-event) on the element.
   *
   * @param element element of document
   * @param eventType event type
   */
  setEvent(element: Element, eventType: string) {
    AttrDataUtil.setEvent(element, eventType);
  }

  /**
   * Get event (data-web-log-event) defined in element.
   *
   * @param element {Element} element of document
   * @returns {string} event type
   */
  getEvent(element: Element): string {
    return AttrDataUtil.getEvent(element);
  }

  /**
   * Element to the request parameter (data-web-log-param).
   *
   * @param element {Element} element of document
   * @param jsonData {object} JSON data
   */
  setRequestParam(element: Element, jsonData: RequestParams) {
    AttrDataUtil.setRequestParam(element, jsonData);
  }

  /**
   * Get the request parameter (data-web-log-param) defined in the element.
   *
   * @param element {Element} element of document
   * @returns {RequestParams | string} JSON object should be RequestParams.
   */
  getRequestParam(element: Element): RequestParams | string {
    return AttrDataUtil.getRequestParam(element);
  }

  /**
   * Send data for PageView or Event Logging.
   *
   * @param params {RequestParams} RequestParams object for request
   * @param options {object} true if log is for page view, otherwise false. (default value is false)
   */
  submit(params: RequestParams, options?: boolean | { isPVLog: boolean; isAsync: boolean }) {
    let isPVLog = false;
    let isAsync = true;

    if (typeof options === 'boolean') {
      isPVLog = options;
    } else if (options && typeof options === 'object') {
      isPVLog = (options.isPVLog && true) || false;
      isAsync = (options.isAsync && true) || false;
    }

    if (isPVLog) {
      this.commonParameter.setCommonWebParameter(new CommonWebParameter());

      if (this.commonData.sdpVisitKey) {
        params.data.sdpVisitKey = this.commonData.sdpVisitKey;
      }
    }

    // update web session id if needed
    this.commonParameter.setWebSessionId();

    const requestJSON = RequestUtil.generateRequestJSON(params, this.commonParameter, this.campaignParameter);

    return this.logRequester.send(requestJSON, isAsync);
  }

  /**
   * Initialize impression tracking.
   * @param params {TrackImpressionParamType}
   * @param params.element: Required if tracking is needed for a specific element.
   * @param params.triggerNow: Trigger impression event now
   * @param params.customData: In case you need to trigger impression with data in JS.
   * @param params.options: Custom thresholds
   */
  trackImpression(params: TrackImpressionParamType) {
    // In case the consumer passes null value unintentionally
    if (params.element === null) {
      return;
    }

    const impression = new Impression(this.parameterStore, this.commonParameter, this.commonData, this.campaignParameter, this.logRequester, params?.options);

    if (params?.triggerNow && params?.element) {
      impression.sendImpressionLog(params.element, params.customData);

      return;
    }

    // In case IntersectionObserver is not supported, we will not use implicit impression tracking.
    if ('IntersectionObserver' in window) {
      impression.initTracking(params?.element);
    }
  }

  /**
   * Initialize implicit click tracking.
   */
  trackClick() {
    const click = new Click(this.parameterStore, this.commonParameter, this.commonData, this.campaignParameter, this.logRequester);
    document.addEventListener('click', click.clickHandler.bind(this), true);
  }

  /**
   * Generate a JSON object to send the common parameter.
   * We keep this method here for consumers currently directly calling this method.
   *
   * @returns {CommonParameter} Returns generated CommonParameter
   */
  private generateCommonParameter(): CommonParameter {
    return new CommonParameter().setPlatform(this.platform);
  }

  /**
   * Generate a JSON object to send the meta log data.
   * We keep this method here for consumers currently directly calling this method.
   *
   * @param meta {object} JSON object for data
   * @returns {MetaParameter} Returns generated MetaParameter
   */
  private generateMetaParameter(meta: MetaParamsType): MetaParameter {
    return RequestUtil.generateMetaParameter(meta);
  }

  /**
   * Generate a JSON object to send the log data.
   * We keep this method here for consumers currently directly calling this method.
   *
   * @param data {object} JSON object for data
   * @returns {DataParameter} Returns generated DataParameter
   */
  private generateDataParameter(data: object): DataParameter {
    return RequestUtil.generateDataParameter(data);
  }

  /**
   * Generate a JSON object to send the extra log data.
   * We keep this method here for consumers currently directly calling this method.
   *
   * @param extra {object} JSON object for extra
   * @returns {ExtraParameter} Returns generated ExtraParameter
   */
  private generateExtraParameter(extra: object): ExtraParameter {
    return new ExtraParameter().setExtraData(extra).setSentTime(new Date().toISOString());
  }

  /**
   * We keep this method here for consumers currently directly calling this method.
   *
   * @param params {RequestParams} JSON object for request
   * @returns {{}} JSON object of CommonParameter + MetaParameter + DataParameter + ExtraParameter + CampaignParameter.
   */
  private generateRequestJSON(params: RequestParams): object {
    return RequestUtil.generateRequestJSON(params, this.commonParameter, this.campaignParameter);
  }

  /**
   * According to the element's coupang web log custom attribute (data-coupang-web-log-event, data-coupang-web-log-params)
   * Bind event to document to automatically send log data.
   *
   * @param eventTypes {Array<string>} types of event
   */
  private bindEvent(eventTypes: Array<string>) {
    const eventHandler = (event: Event) => {
      const type = event.type;
      let target = event.target as Node;
      let foundElement = false;
      let eventType;

      while (!foundElement && target) {
        eventType = this.getEvent(target as Element);
        if (eventType && eventType === type) {
          foundElement = true;
          break;
        }
        target = target.parentNode;
      }

      if (foundElement === true) {
        ConsoleLog.d(TAG, `#bindEvent - matched ${type} eventType`);
        const eventData = this.getRequestParam(target as Element);
        this.submit(this.parameterStore.replaceDynamicString(eventData));
      }
    };

    WindowEventListener.add('load', () => {
      eventTypes.forEach(evnType => {
        ConsoleLog.d(TAG, `#bindEvent - addEventListener eventType: ${evnType}`);
        document.addEventListener(evnType, eventHandler, true);
      });
    });
  }
}
